import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { DollarSign, TrendingUp, Calendar, PieChart, Download } from 'lucide-react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, addMonths, getDaysInMonth } from 'date-fns';

const IncomeTracker = () => {
  const { currentUser } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [stats, setStats] = useState({
    totalEarned: 0,
    totalPending: 0,
    completedAppointments: 0,
    pendingAppointments: 0,
    projectedTotal: 0,
  });

  // Fetch appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const appointmentsRef = collection(db, 'appointments');
        const q = query(appointmentsRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        const appointmentsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setAppointments(appointmentsData);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [currentUser]);

  // Calculate stats for selected month
  useEffect(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);

    const monthAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= monthStart && aptDate <= monthEnd;
    });

    const earned = monthAppointments
      .filter(apt => apt.paymentStatus === 'paid')
      .reduce((sum, apt) => sum + (parseFloat(apt.amount) || 0), 0);

    const pending = monthAppointments
      .filter(apt => apt.paymentStatus === 'pending' || !apt.paymentStatus)
      .reduce((sum, apt) => sum + (parseFloat(apt.amount) || 0), 0);

    const completed = monthAppointments.filter(apt => apt.paymentStatus === 'paid').length;
    const pendingCount = monthAppointments.filter(apt => apt.paymentStatus !== 'paid').length;

    // Calculate projection
    const today = new Date();
    const daysInMonth = getDaysInMonth(selectedMonth);
    const daysPassed = today.getMonth() === selectedMonth.getMonth() && today.getFullYear() === selectedMonth.getFullYear()
      ? today.getDate()
      : daysInMonth;
    
    const avgPerDay = (earned + pending) / daysPassed;
    const projected = avgPerDay * daysInMonth;

    setStats({
      totalEarned: earned,
      totalPending: pending,
      completedAppointments: completed,
      pendingAppointments: pendingCount,
      projectedTotal: projected,
    });
  }, [appointments, selectedMonth]);

  const updatePaymentStatus = async (appointmentId, status) => {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        paymentStatus: status,
        updatedAt: new Date().toISOString(),
      });

      // Refresh appointments
      const appointmentsRef = collection(db, 'appointments');
      const q = query(appointmentsRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const appointmentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Failed to update payment status');
    }
  };

  const exportToCSV = () => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);

    const monthAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= monthStart && aptDate <= monthEnd;
    });

    const csv = [
      ['Date', 'Client', 'Pet', 'Service', 'Amount', 'Status'].join(','),
      ...monthAppointments.map(apt => [
        apt.date,
        apt.clientName || '',
        apt.petName || '',
        apt.serviceType || '',
        apt.amount || '0',
        apt.paymentStatus || 'pending'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `income_${format(selectedMonth, 'yyyy_MM')}.csv`;
    a.click();
  };

  const serviceRates = {
    walking: 25,
    sitting: 50,
    daycare: 40,
    checkin: 20,
  };

  const getServiceColor = (type) => {
    const colors = {
      walking: 'bg-service-walking',
      sitting: 'bg-service-sitting',
      daycare: 'bg-service-daycare',
      checkin: 'bg-service-checkin',
    };
    return colors[type] || 'bg-gray-200';
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DollarSign className="h-8 w-8 text-pastel-green" />
            <div>
              <h1 className="text-3xl font-bold text-brand-brown">Income Tracker</h1>
              <p className="text-brand-brown/60">Track your earnings and financial insights</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={exportToCSV}
              className="btn-secondary flex items-center space-x-2"
            >
              <Download className="h-5 w-5" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Month Selector */}
        <div className="card p-6">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setSelectedMonth(addMonths(selectedMonth, -1))}
              className="btn-secondary px-6 py-3 flex items-center space-x-2"
            >
              <span>←</span>
              <span>Previous</span>
            </button>
            <h2 className="text-2xl font-bold text-brand-brown flex-shrink-0 px-4">
              {format(selectedMonth, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))}
              className="btn-secondary px-6 py-3 flex items-center space-x-2"
              disabled={selectedMonth >= new Date()}
            >
              <span>Next</span>
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pastel-green"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Earned */}
              <div className="card p-6 bg-gradient-to-br from-pastel-green/20 to-pastel-green/5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-brand-brown/70">Earned This Month</h3>
                  <DollarSign className="h-5 w-5 text-pastel-green" />
                </div>
                <p className="text-3xl font-bold text-brand-brown">
                  ${stats.totalEarned.toFixed(2)}
                </p>
                <p className="text-sm text-brand-brown/60 mt-1">
                  {stats.completedAppointments} completed
                </p>
              </div>

              {/* Pending */}
              <div className="card p-6 bg-gradient-to-br from-pastel-yellow/20 to-pastel-yellow/5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-brand-brown/70">Pending Payment</h3>
                  <Calendar className="h-5 w-5 text-yellow-600" />
                </div>
                <p className="text-3xl font-bold text-brand-brown">
                  ${stats.totalPending.toFixed(2)}
                </p>
                <p className="text-sm text-brand-brown/60 mt-1">
                  {stats.pendingAppointments} pending
                </p>
              </div>

              {/* Projected */}
              <div className="card p-6 bg-gradient-to-br from-pastel-blue/20 to-pastel-blue/5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-brand-brown/70">Month Projection</h3>
                  <TrendingUp className="h-5 w-5 text-pastel-blue" />
                </div>
                <p className="text-3xl font-bold text-brand-brown">
                  ${stats.projectedTotal.toFixed(2)}
                </p>
                <p className="text-sm text-brand-brown/60 mt-1">
                  Based on current rate
                </p>
              </div>

              {/* Total */}
              <div className="card p-6 bg-gradient-to-br from-pastel-purple/20 to-pastel-purple/5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-brand-brown/70">Month Total</h3>
                  <PieChart className="h-5 w-5 text-pastel-purple" />
                </div>
                <p className="text-3xl font-bold text-brand-brown">
                  ${(stats.totalEarned + stats.totalPending).toFixed(2)}
                </p>
                <p className="text-sm text-brand-brown/60 mt-1">
                  {stats.completedAppointments + stats.pendingAppointments} total appointments
                </p>
              </div>
            </div>

            {/* Appointments List */}
            <div className="card p-6">
              <h2 className="text-xl font-bold text-brand-brown mb-4">Appointments</h2>
              
              {appointments
                .filter(apt => {
                  const aptDate = new Date(apt.date);
                  const monthStart = startOfMonth(selectedMonth);
                  const monthEnd = endOfMonth(selectedMonth);
                  return aptDate >= monthStart && aptDate <= monthEnd;
                })
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map(appointment => (
                  <div
                    key={appointment.id}
                    className={`p-4 rounded-xl mb-3 border-2 ${getServiceColor(appointment.serviceType)} border-brand-brown/10`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-brand-brown">
                          {appointment.petName} - {appointment.clientName}
                        </h3>
                        <p className="text-sm text-brand-brown/60">
                          {format(new Date(appointment.date), 'MMM d, yyyy')} • {appointment.serviceType}
                        </p>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <input
                            type="number"
                            value={appointment.amount || serviceRates[appointment.serviceType] || 0}
                            onChange={(e) => {
                              const appointmentRef = doc(db, 'appointments', appointment.id);
                              updateDoc(appointmentRef, { amount: e.target.value });
                            }}
                            className="w-24 px-3 py-1 rounded-lg border border-brand-brown/20 text-right font-bold"
                            placeholder="0.00"
                          />
                        </div>

                        <select
                          value={appointment.paymentStatus || 'pending'}
                          onChange={(e) => updatePaymentStatus(appointment.id, e.target.value)}
                          className={`px-3 py-1 rounded-lg font-medium ${
                            appointment.paymentStatus === 'paid'
                              ? 'bg-pastel-green text-brand-brown'
                              : 'bg-pastel-yellow text-brand-brown'
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

              {appointments.filter(apt => {
                const aptDate = new Date(apt.date);
                const monthStart = startOfMonth(selectedMonth);
                const monthEnd = endOfMonth(selectedMonth);
                return aptDate >= monthStart && aptDate <= monthEnd;
              }).length === 0 && (
                <p className="text-center text-brand-brown/60 py-8">
                  No appointments this month
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default IncomeTracker;
